/**
 * Client Detail Page
 * Detailed view of a single client with tabs for different information
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as OrderIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
  PersonAdd as AssignIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { useClient, useClientAnalytics, useClientOrders, useUpdateClient, useAssignClientStaff } from '../hooks/useClients';
import { useClientTierConfigs } from '../hooks/useClientTiers';
import { useQuery } from '@tanstack/react-query';
import { clientActivitiesApi } from '../api/clientApi';
import { useTaskAssignees } from '../hooks/useTasks';
import { toast } from 'react-toastify';
import type { UpdateClientInput } from '../types/client';
import ClientTierBadge from '../components/ClientTierBadge';
import ClientStatusChip from '../components/ClientStatusChip';
import HealthScoreIndicator from '../components/HealthScoreIndicator';
import AddNoteForm, { AddNoteFormData } from '../components/AddNoteForm';
import ActivityTimeline, { Activity } from '../components/ActivityTimeline';
import ComposeEmailDialog from '../components/ComposeEmailDialog';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && children}
    </div>
  );
};

export const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Dialog states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<string>('');
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const { data: activities = [], isError: activitiesError } = useQuery({
    queryKey: ['clientActivities', id],
    queryFn: () => clientActivitiesApi.getActivities(id || ''),
    enabled: !!id,
    staleTime: 0,
  });
  const [editFormData, setEditFormData] = useState<UpdateClientInput>({});

  // Fetch client data
  const { data: client, isLoading, error } = useClient(id || '');
  const { data: analytics } = useClientAnalytics(id || '');
  const { data: ordersData } = useClientOrders(id || '', { limit: 10 });
  const { data: assignees } = useTaskAssignees();
  const updateClientMutation = useUpdateClient();
  const { data: tierConfigs } = useClientTierConfigs();
  const assignClientStaffMutation = useAssignClientStaff();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD'
    }).format(value);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load client details. Please try again.</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/ai-crm/clients')} sx={{ mt: 2 }}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  // Action handlers
  const handleSendEmail = () => {
    setEmailDialogOpen(true);
  };

  const handleAssignStaff = () => {
    setAssignDialogOpen(true);
  };

  const handleAssignConfirm = async () => {
    if (selectedStaff && client) {
      try {
        await assignClientStaffMutation.mutateAsync({
          id: client.id,
          assignedToUserId: selectedStaff
        });
        toast.success('Staff assigned successfully 員工指派成功');
        setAssignDialogOpen(false);
        setSelectedStaff('');
      } catch (err: any) {
        toast.error(err.message || 'Failed to assign staff 指派員工失敗');
      }
    }
  };

  const handleAddActivity = async (data: AddNoteFormData) => {
    if (!id) return;
    try {
      await clientActivitiesApi.addActivity(id, {
        type: data.type,
        subject: data.subject,
        content: data.content,
        metadata: data.metadata,
      });
      toast.success('Activity logged 已新增活動');
      setIsAddingActivity(false);
      // Refetch timeline + the client itself (lastContactDate + totalInteractions change)
      queryClient.invalidateQueries({ queryKey: ['clientActivities', id] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to log activity');
    }
  };

  const handleEditClient = () => {
    // Populate form with current client data
    setEditFormData({
      clientName: client.clientName,
      clientType: client.clientType,
      industry: client.industry || '',
      companySize: client.companySize || '',
      website: client.website || '',
      primaryContact: client.primaryContact,
      email: client.email,
      phone: client.phone || '',
      secondaryContact: client.secondaryContact || '',
      secondaryEmail: client.secondaryEmail || '',
      businessAddress: client.businessAddress || '',
      paymentTerms: client.paymentTerms,
      status: client.status,
      tier: client.tier,
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: keyof UpdateClientInput, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value as any }));
  };

  const handleEditSubmit = async () => {
    if (!client) return;
    try {
      await updateClientMutation.mutateAsync({ id: client.id, data: editFormData });
      toast.success('Client updated successfully 客戶已更新');
      setEditDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update client 更新客戶失敗');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header - Mobile Optimized */}
      <Box sx={{ mb: 3 }}>
        {/* Top row: Back button + Name + Status badges */}
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 1, sm: 2 },
          mb: 2
        }}>
          <IconButton
            onClick={() => navigate('/ai-crm/clients')}
            sx={{ mt: 0.5 }}
          >
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 0.5
            }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 700,
                  wordBreak: 'break-word'
                }}
              >
                {client.clientName}
              </Typography>
              <ClientStatusChip status={client.status} />
              <ClientTierBadge tier={client.tier} />
            </Box>
            {client.industry && (
              <Typography variant="body2" color="text.secondary">
                {client.industry}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action Buttons Row */}
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          ml: { xs: 0, sm: 6 }
        }}>
          {client?.email && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              onClick={handleSendEmail}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Send Email
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AssignIcon />}
            onClick={handleAssignStaff}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            Assign Staff
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
            onClick={handleEditClient}
          >
            Edit Client
          </Button>
        </Box>
      </Box>

      <ComposeEmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clientActivities', id] });
        }}
        recipientName={client.primaryContact || client.clientName}
        recipientEmail={client.email}
        recipientCompany={client.clientName}
        clientId={client.id}
      />

      {/* Assign Staff Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignIcon color="primary" />
            <Typography variant="h6">Assign Staff 指派員工</Typography>
          </Box>
          <IconButton onClick={() => setAssignDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a staff member to assign to {client.clientName}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Staff Member 員工</InputLabel>
            <Select
              value={selectedStaff}
              label="Staff Member 員工"
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              {assignees?.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {staff.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{staff.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {staff.role} • {staff.email}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignClientStaffMutation.isPending}>
            Cancel 取消
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignConfirm}
            disabled={!selectedStaff || assignClientStaffMutation.isPending}
          >
            {assignClientStaffMutation.isPending ? 'Assigning... 指派中...' : 'Assign 指派'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">Edit Client 編輯客戶</Typography>
          </Box>
          <IconButton onClick={() => setEditDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Client Name 客戶名稱"
              value={editFormData.clientName || ''}
              onChange={(e) => handleEditFormChange('clientName', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Primary Contact 主要聯絡人"
              value={editFormData.primaryContact || ''}
              onChange={(e) => handleEditFormChange('primaryContact', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Email 電郵"
              type="email"
              value={editFormData.email || ''}
              onChange={(e) => handleEditFormChange('email', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Phone 電話"
              value={editFormData.phone || ''}
              onChange={(e) => handleEditFormChange('phone', e.target.value)}
              fullWidth
            />
            <TextField
              label="Industry 行業"
              value={editFormData.industry || ''}
              onChange={(e) => handleEditFormChange('industry', e.target.value)}
              fullWidth
            />
            <TextField
              label="Website 網站"
              value={editFormData.website || ''}
              onChange={(e) => handleEditFormChange('website', e.target.value)}
              fullWidth
            />
            <TextField
              label="Company Size 公司規模"
              value={editFormData.companySize || ''}
              onChange={(e) => handleEditFormChange('companySize', e.target.value)}
              fullWidth
              placeholder="e.g., 10-50"
            />
            <TextField
              label="Business Address 公司地址"
              value={editFormData.businessAddress || ''}
              onChange={(e) => handleEditFormChange('businessAddress', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Secondary Contact 次要聯絡人"
              value={editFormData.secondaryContact || ''}
              onChange={(e) => handleEditFormChange('secondaryContact', e.target.value)}
              fullWidth
            />
            <TextField
              label="Secondary Email 次要電郵"
              type="email"
              value={editFormData.secondaryEmail || ''}
              onChange={(e) => handleEditFormChange('secondaryEmail', e.target.value)}
              fullWidth
            />
            <TextField
              label="Payment Terms 付款條款"
              value={editFormData.paymentTerms || ''}
              onChange={(e) => handleEditFormChange('paymentTerms', e.target.value)}
              fullWidth
              placeholder="e.g., NET30"
            />
            <FormControl fullWidth>
              <InputLabel id="edit-client-status-label">Status 狀態</InputLabel>
              <Select
                labelId="edit-client-status-label"
                label="Status 狀態"
                value={editFormData.status || ''}
                onChange={(e) => handleEditFormChange('status', e.target.value as string)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="CHURNED">Churned</MenuItem>
                <MenuItem value="AT_RISK">At Risk</MenuItem>
                <MenuItem value="VIP">VIP</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-client-tier-label">Tier 級別</InputLabel>
              <Select
                labelId="edit-client-tier-label"
                label="Tier 級別"
                value={editFormData.tier || ''}
                onChange={(e) => handleEditFormChange('tier', e.target.value as string)}
              >
                {/* Render existing-but-unconfigured tier value so it's preserved, not silently dropped */}
                {editFormData.tier &&
                  !tierConfigs?.some((c) => c.name.toUpperCase() === editFormData.tier!.toUpperCase()) && (
                    <MenuItem value={editFormData.tier}>{editFormData.tier} (legacy)</MenuItem>
                  )}
                {(tierConfigs ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: c.color }} />
                      {c.name}
                    </Box>
                  </MenuItem>
                ))}
                {(!tierConfigs || tierConfigs.length === 0) && (
                  <MenuItem disabled value="">
                    No tiers configured — set them in Settings → Client Tiers
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel 取消
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={updateClientMutation.isPending || !editFormData.clientName || !editFormData.email}
          >
            {updateClientMutation.isPending ? 'Saving... 儲存中...' : 'Save 儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Key Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon color="primary" />
                <Typography variant="caption" color="text.secondary">
                  Lifetime Value
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatCurrency(client.lifetimeValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="success" />
                <Typography variant="caption" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatCurrency(client.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <OrderIcon color="info" />
                <Typography variant="caption" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {client.totalOrders}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg: {formatCurrency(client.averageOrderValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Health Score
              </Typography>
              <HealthScoreIndicator score={client.healthScore} showLabel={false} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {client.healthScore}/100
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab label="Overview" />
          <Tab label="Activities 活動" />
          {/* Orders + Analytics intentionally hidden — they're scaffolded for
              a future invoiced-deals workflow we don't ship yet. The TabPanels
              below stay in place (no-op when their index isn't selected) so we
              can re-enable by un-commenting the Tabs above. */}
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Contact Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Contact Information
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Primary Contact
                      </Typography>
                      <Typography variant="body1">{client.primaryContact}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">{client.email}</Typography>
                    </Box>
                  </Box>
                  {client.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">{client.phone}</Typography>
                      </Box>
                    </Box>
                  )}
                  {client.website && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WebsiteIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Website
                        </Typography>
                        <Typography variant="body1">{client.website}</Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>

                {client.secondaryContact && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Secondary Contact
                    </Typography>
                    <Typography variant="body1">{client.secondaryContact}</Typography>
                    {client.secondaryEmail && (
                      <Typography variant="body2" color="text.secondary">
                        {client.secondaryEmail}
                      </Typography>
                    )}
                  </>
                )}
              </Grid>

              {/* Business Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Business Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Client Type
                    </Typography>
                    <Typography variant="body1">{client.clientType.replace('_', ' ')}</Typography>
                  </Box>
                  {client.companySize && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Company Size
                      </Typography>
                      <Typography variant="body1">{client.companySize} employees</Typography>
                    </Box>
                  )}
                  {client.businessAddress && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Business Address
                      </Typography>
                      <Typography variant="body1">{client.businessAddress}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Terms
                    </Typography>
                    <Typography variant="body1">{client.paymentTerms}</Typography>
                  </Box>
                  {client.creditLimit && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Credit Limit
                      </Typography>
                      <Typography variant="body1">{formatCurrency(client.creditLimit)}</Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>

              {/* Tags */}
              {client.tags && client.tags.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {client.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Engagement Metrics */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Engagement Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Total Interactions
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {client.totalInteractions}
                      </Typography>
                    </Paper>
                  </Grid>
                  {client.lastContactDate && (
                    <Grid item xs={12} sm={4}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Last Contact
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatDate(client.lastContactDate)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {client.lastOrderDate && (
                    <Grid item xs={12} sm={4}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Last Order
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatDate(client.lastOrderDate)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Activities Tab */}
        <TabPanel value={activeTab} index={1}>
          <CardContent>
            <Stack spacing={2}>
              {!isAddingActivity && (
                <Button
                  variant="outlined"
                  onClick={() => setIsAddingActivity(true)}
                  fullWidth
                >
                  Add Activity 新增活動
                </Button>
              )}

              {isAddingActivity && (
                <AddNoteForm
                  onSubmit={handleAddActivity}
                  onCancel={() => setIsAddingActivity(false)}
                  compact
                  recipientContext={{
                    name: client.primaryContact || client.clientName,
                    company: client.clientName,
                    email: client.email,
                  }}
                />
              )}

              {activitiesError && (
                <Alert severity="error">Failed to load activities. Please refresh the page.</Alert>
              )}
              <ActivityTimeline
                activities={activities}
                maxHeight={500}
                emptyMessage="No activities yet. Start by adding a note! 尚無活動記錄，開始新增備註吧！"
              />
            </Stack>
          </CardContent>
        </TabPanel>

        {/* Orders Tab */}
        <TabPanel value={activeTab} index={2}>
          <CardContent>
            {ordersData && ordersData.data.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Order Number</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Payment</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersData.data.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {order.orderNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(order.orderDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            color={
                              order.status === 'DELIVERED' ? 'success' :
                              order.status === 'CANCELLED' ? 'error' :
                              order.status === 'SHIPPED' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.paymentStatus}
                            size="small"
                            color={
                              order.paymentStatus === 'PAID' ? 'success' :
                              order.paymentStatus === 'OVERDUE' ? 'error' :
                              'warning'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(order.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No orders found for this client.</Alert>
            )}
          </CardContent>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={activeTab} index={3}>
          <CardContent>
            {analytics ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Financial Summary
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Lifetime Value
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(analytics.financial.lifetimeValue)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Revenue
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(analytics.financial.totalRevenue)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Average Order Value
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(analytics.financial.averageOrderValue)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Outstanding Balance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: analytics.financial.outstandingBalance > 0 ? 'error.main' : 'success.main' }}>
                        {formatCurrency(analytics.financial.outstandingBalance)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Engagement Summary
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Interactions
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {analytics.engagement.totalInteractions}
                      </Typography>
                    </Box>
                    {analytics.engagement.lastContactDate && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Last Contact
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatDate(analytics.engagement.lastContactDate)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {analytics.recentActivity.recentOrders.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Recent Orders
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Order Number</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.recentActivity.recentOrders.map((order) => (
                            <TableRow key={order.orderNumber}>
                              <TableCell>{order.orderNumber}</TableCell>
                              <TableCell>{formatDate(order.orderDate)}</TableCell>
                              <TableCell>{order.status}</TableCell>
                              <TableCell align="right">{formatCurrency(order.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Alert severity="info">Analytics data is being calculated...</Alert>
            )}
          </CardContent>
        </TabPanel>

      </Card>
    </Box>
  );
};

export default ClientDetailPage;
